import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setJobDetail } from "@store/actions/job.action";
import { getJobs, updateJob } from "@endpoints/job.service";
import { EnumJobStatus2, JobStatus } from "@utils/constants";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import PlusIcon from "@assets/svg/PlusIcon";
import { NavLink } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

export function getStatusColors({ status }: { status: EnumJobStatus2 }) {
  switch (status) {
    case EnumJobStatus2.LIVE:
      return 'border-[#99CA60]'
    case EnumJobStatus2.EXPIRED:
      return 'border-[#6f6f6f]'
    default:
      return 'border-black'
  }
}

export function getStatusTextColors({ status }: { status: EnumJobStatus2 }) {
  switch (status) {
    case EnumJobStatus2.LIVE:
      return 'text-[#99CA60]'
    case EnumJobStatus2.EXPIRED:
      return 'text-[#6f6f6f]'
    default:
      return 'text-black'
  }
}


const JobCard = ({ data, handleCloseJob, index }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isClosed = data?.status === JobStatus.CLOSED && "Closed";
  const isExpired = data?.status === JobStatus.EXPIRED && "Expired";

  return (
    <NavLink
      to={`/job/${data?._id}/applicants`}
      state={data}
      onClick={() => {
        dispatch(setJobDetail(data));
        // navigate(`/job/${data?._id}/applicants`, { state: data });
      }}
      className="grid grid-cols-[1.3fr_1.3fr_0.3fr] gap-12 justify-between bg-white border-b border-[#ececec] rounded px-3 py-5 hover:bg-slate-50 cursor-pointer"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-light-black font-medium pl-2">
            {data?.refNo}
          </span>
          <span className={`font-medium text-xs ${getStatusTextColors({status:data?.status})}`}> {data?.status === JobStatus.LIVE && "Live"}
            {data?.status === JobStatus.DRAFT && "Draft"}
            {data?.status === JobStatus.UNPUBLISHED && "Unpublished"}
            {data?.status === JobStatus.CLOSED && "Closed"}
            {data?.status === JobStatus.EXPIRED && "Expired"}</span>
        </div>
        <div className={`flex flex-col gap-1 rounded-md py-3 px-5 border border-y-gray-100 border-r-gray-100 ${getStatusColors({ status: data?.status })} border-l-[3px]`}>
          <span className="text-xs font-bold text-primary line-clamp-1">{data?.title}</span>
          <span className="text-xss font-medium text-light-black">Hiring Company:&nbsp;&nbsp;&nbsp;<span className="font-medium text-primary">{data?.hiringCompany?.companyName || 'N/A'}</span></span>
        </div>
        <div className="flex items-center justify-between pl-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 font-medium">
              Published:
            </span>
            <span className="text-[10px] text-primary italic font-medium">
              {data?.publishedDate &&
                dayjs(data?.publishedDate).format("hh:mm a Do MMM YY")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 font-medium">
              {isClosed ? "Closed" : "Expiring"}:
            </span>
            <span className="text-[10px] text-primary italic font-medium">
              {dayjs(isClosed ? data?.updatedAt : data?.expiredAt).format(
                "hh:mm a Do MMM YY"
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-5">
          <span className="text-xss text-light-black font-medium italic">
            {dayjs(data?.latestTracker?.updatedAt).format("hh:mm a")}
          </span>
          <span className="text-xss text-light-black font-medium italic">
            {dayjs(data?.latestTracker?.updatedAt).format("DD MMM YYYY")}
          </span>
        </div>
        <div className="flex items-center bg-light-white-700 border border-gray-200 rounded-md h-[3.8rem] py-3 px-3">
          <span className="text-xs font-medium text-primary">
            Latest Activity : {data?.latestTracker?.status || 'N/A'}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center text-xss gap-2">
        Applicants
        <div className="relative w-[53px] h-[53px] flex items-center justify-center">
          {/* Glow layer */}
          {data?.unViewedApplicants ? (
            <span className="absolute inset-0 rounded-full bg-green-500 opacity-5 animate-pulse"></span>
          ) : null}

          {/* Badge */}
          <div
            className={`relative z-10 w-[53px] h-[53px] rounded-full border-[3.5px] ${data?.totalApplicants ? "border-green-500" : "border-gray-400"
              } flex items-center justify-center`}
          >
            <span className="text-xs font-semibold text-black">
              {data?.totalApplicants}
            </span>
          </div>
        </div>
      </div>

      {/* <div className="flex font-medium text-xs text-primary rounded-md bg-[#fafafa] h-8 border w-20 justify-center items-center self-center">
        Expired
      </div> */}
      {/* <div className={`flex items-center justify-center bg-[#fafafa] rounded-md h-8 w-20 text-xs font-medium text-primary self-center mb-4 border-l-2 ${getStatusColors({ status: data?.status })}`}>
        {data?.status === JobStatus.LIVE && "Live"}
        {data?.status === JobStatus.DRAFT && "Draft"}
        {data?.status === JobStatus.UNPUBLISHED && "Unpublished"}
        {data?.status === JobStatus.CLOSED && "Closed"}
        {data?.status === JobStatus.EXPIRED && "Expired"}
      </div> */}
    </NavLink>
  );
};

export const jobStatusFilters = [
  {
    label: 'Active',
    value: EnumJobStatus2.LIVE
  },
  {
    label: 'Draft',
    value: EnumJobStatus2.DRAFT
  },
  {
    label: 'Expired',
    value: EnumJobStatus2.EXPIRED
  },
  {
    label: 'All',
    value: 'All'
  },
]

const LIMIT = 15

const ViewByJob = () => {
  const navigate = useNavigate();
  const scrollListRef = useRef(null)
  const sentinelRef = useRef(null)
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(false)
  const [activeFilter, setActiveFilter] = useState<any>('All')
  const [searchText, setSearchText] = useState('')

  const [jobs, setJobs] = useState([])

  const fetchJobs = useCallback(
    async () => {
      try {
        if (page > 1) {
          setIsLoadingMore(true)
        } else {
          setIsLoading(true)
        }
        const { success, data, pagination } = await getJobs({
          limit: LIMIT,
          page,
          jobStatus: typeof activeFilter === 'string' ? undefined : activeFilter
        })
        if (success) {
          setJobs((prev) => {
            return page === 1 ? data : [...prev, ...data]
          })
          setHasMoreData(pagination?.hasMore)
        }
      } catch (error) {
        enqueueSnackbar(error, { variant: 'error' })
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [page, activeFilter, searchText]
  )

  function handleLoadMore() {
    if (!hasMoreData || isLoadingMore || isLoading) return
    setPage((prev) => prev + 1)
  }

  const handleCloseJob = async (jobId, status) => {
    try {
      const { success } = await updateJob({ jobId, status });
      if (success) {
        fetchJobs();
        enqueueSnackbar("Job Closed Successfully!", { variant: "success" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    if (!scrollListRef?.current || !sentinelRef?.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries?.[0]?.isIntersecting) {
        handleLoadMore()
      }
    }, {
      root: scrollListRef?.current,
      rootMargin: '0px',
      threshold: 1
    })
    observer.observe(sentinelRef?.current)
    return () => observer.disconnect()
  }, [isLoading, isLoadingMore])

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="flex items-center py-3.5 border-b gap-28">
        {/* Search container */}
        <div className="flex items-center w-max border rounded-md bg-gray-50 px-3">
          <input
            className="w-64 text-xs font-medium bg-transparent outline-none h-9"
            placeholder="Search..."
          />
          <SearchIcon color="gray" size={16} />
        </div>
        {/* Filters container */}
        <div className="flex items-center gap-3">
          <span className="text-primary text-xs font-medium">Filters by :</span>
          <div className="flex items-center gap-5">
            {
              jobStatusFilters?.map((filter) => {
                const isSelected = filter?.value === activeFilter
                return <button
                  onClick={() => setActiveFilter(filter?.value)}
                  className="flex items-center gap-2">
                  <span className="font-medium text-xs text-primary">{filter?.label}</span>
                  <div className="flex h-5 w-5 rounded-full border-[1.5px] border-primary">
                    {isSelected ?
                      <div className="m-auto rounded-full h-3 w-3 bg-primary" />
                      :
                      null}
                  </div>
                </button>
              })
            }
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="gap-2.5 flex flex-col mt-3">
          {Array.from({ length: 10 }, (x, i) => i).map((key) => {
            return (
              <div className="flex justify-between">
                <div
                  style={{
                    borderWidth: 1,
                  }}
                  className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5"
                >
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                  <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                </div>
              </div>
            );
          })}
        </div>
      ) : !jobs?.length ? (
        <div className="flex flex-1 h-screen flex-col items-center justify-center">
          <p className="text-primary font-semibold">No Job created</p>
          <p className="mt-2 mb-5">Create a Job now!</p>
          <button
            onClick={() => navigate("create")}
            className="flex items-center justify-center border bg-green-600 rounded-md px-2 py-1 gap-1 text-white font-medium text-sm"
          >
            <PlusIcon className={"w-4 h-4"} fill={"white"} />
            Create Job Post
          </button>
        </div>
      ) : (
        <>
          <div
            ref={scrollListRef}
            className="flex flex-col overflow-y-scroll no-scrollbar">
            {jobs?.map((job, jobIndex) => {
              return (
                <JobCard
                  key={job?._id}
                  data={job}
                  handleCloseJob={handleCloseJob}
                  index={jobIndex}
                />
              );
            })}
            <div ref={sentinelRef} className="h-px"></div>
          </div>
          {
            isLoadingMore ?
              <div className="flex items-center mx-auto text-xs font-medium text-light-black italic gap-2">
                <LoadingAnimation color="black" />
                Loading more data...
              </div>
              :
              null
          }
        </>
      )}
    </div>
  );
};

export default ViewByJob;
